import { AltaMensual, BajaMensual, MonthAggregate, PurchaserPlanRaw, DateRange, Granularity, Bucket, ManualRevenueEntry } from '../../types/Dashboard';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MES_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const ACTIVE_STATUSES = new Set(['Activo', 'Pendiente activación downgrade']);
const CHURN_STATUSES = new Set(['Inactivo', 'Baja', 'Cancelado']);

// ---------------------------------------------------------------------------
// Legacy helpers — kept for retrocompat with existing components
// ---------------------------------------------------------------------------

export function monthKey(anio: number, mes: number): string {
  return `${anio}-${String(mes).padStart(2, '0')}`;
}

export function monthLabel(anio: number, mes: number): string {
  const idx = Math.max(0, Math.min(11, mes - 1));
  return `${MES_LABELS[idx]} ${String(anio).slice(-2)}`;
}

/**
 * Agrupa altas y bajas por mes y devuelve un array ordenado cronológicamente.
 * @deprecated Use bucketize + altasInBucket + bajasInBucket instead.
 */
export function aggregateByMonth(
  altas: AltaMensual[],
  bajas: BajaMensual[]
): MonthAggregate[] {
  const byKey = new Map<string, MonthAggregate>();

  altas.forEach((a) => {
    const key = monthKey(a.anio, a.mes);
    let agg = byKey.get(key);
    if (!agg) {
      agg = { monthKey: key, anio: a.anio, mes: a.mes, perPlan: {}, totalAltas: 0, bajas: 0 };
      byKey.set(key, agg);
    }
    const planName = a.plan ?? 'Desconocido';
    agg.perPlan[planName] = (agg.perPlan[planName] ?? 0) + a.cantidad;
    agg.totalAltas += a.cantidad;
  });

  bajas.forEach((b) => {
    const key = monthKey(b.anio, b.mes);
    let agg = byKey.get(key);
    if (!agg) {
      agg = { monthKey: key, anio: b.anio, mes: b.mes, perPlan: {}, totalAltas: 0, bajas: 0 };
      byKey.set(key, agg);
    }
    agg.bajas += b.cantidad;
  });

  return Array.from(byKey.values()).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
}

export function lastNMonths<T extends { monthKey: string }>(items: T[], n: number): T[] {
  return items.slice(-n);
}

/** @deprecated Use planNamesFromRaw for new code. */
export function planNamesFromAltas(altas: AltaMensual[]): string[] {
  const set = new Set<string>();
  altas.forEach((a) => { if (a.plan) set.add(a.plan); });
  return Array.from(set);
}

// ---------------------------------------------------------------------------
// Date utilities — all UTC to avoid timezone drift
// ---------------------------------------------------------------------------

const AR_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Argentina/Buenos_Aires',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Today in Argentina time as YYYY-MM-DD (the backend sends Salesforce
 * DateTimes with the Argentina offset, so "today" must be the Argentine day).
 */
export function today(): string {
  const p: Record<string, string> = {};
  AR_DATE_FORMAT.formatToParts(new Date()).forEach(({ type, value }) => { p[type] = value; });
  return `${p.year}-${p.month}-${p.day}`;
}

function toUTC(dateStr: string): Date {
  // Interpret YYYY-MM-DD as UTC midnight to avoid local-tz shifts
  return new Date(`${dateStr}T00:00:00Z`);
}

function fromUTC(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

export function addDays(date: string, n: number): string {
  const d = toUTC(date);
  d.setUTCDate(d.getUTCDate() + n);
  return fromUTC(d);
}

export function addMonths(date: string, n: number): string {
  const d = toUTC(date);
  const targetMonth = d.getUTCMonth() + n;
  // Set day to 1 first to avoid overshooting short months
  d.setUTCDate(1);
  d.setUTCMonth(targetMonth);
  return fromUTC(d);
}

export function startOfMonth(date: string): string {
  const d = toUTC(date);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

/** Last day of the month (inclusive) */
export function lastDayOfMonth(date: string): string {
  const d = toUTC(date);
  // Day 0 of the NEXT month = last day of this month
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
  return fromUTC(last);
}

/** Alias for lastDayOfMonth */
export function endOfMonth(date: string): string {
  return lastDayOfMonth(date);
}

/** Monday of the ISO week containing `date` */
export function startOfISOWeek(date: string): string {
  const d = toUTC(date);
  // getUTCDay(): 0=Sun, 1=Mon, ..., 6=Sat
  const dow = d.getUTCDay();
  // Distance to Monday: Mon=0, Tue=-1, ..., Sun=-6
  const diff = dow === 0 ? -6 : 1 - dow;
  d.setUTCDate(d.getUTCDate() + diff);
  return fromUTC(d);
}

/** Sunday of the ISO week containing `date` */
export function endOfISOWeek(date: string): string {
  return addDays(startOfISOWeek(date), 6);
}

export function formatDateAR(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

/** Truncates an ISO timestamp to YYYY-MM-DD */
function toDateOnly(iso: string): string {
  return iso.slice(0, 10);
}

// ---------------------------------------------------------------------------
// ISO 8601 week number
// ---------------------------------------------------------------------------

/** Returns { year, week } according to ISO 8601 */
function isoWeek(date: string): { year: number; week: number } {
  const d = toUTC(date);
  // ISO: week 1 contains the first Thursday of the year.
  // Shift so Monday=0, ..., Sunday=6
  const dow = (d.getUTCDay() + 6) % 7;
  // Nearest Thursday (in the same ISO week)
  const thursday = new Date(d);
  thursday.setUTCDate(d.getUTCDate() - dow + 3);
  const year = thursday.getUTCFullYear();
  // Week number = days from Jan 4 (always in W01) / 7 + 1
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const week = Math.round((thursday.getTime() - jan4.getTime()) / (7 * 86400000)) + 1;
  return { year, week };
}

// ---------------------------------------------------------------------------
// Core data functions
// ---------------------------------------------------------------------------

/**
 * ¿La fila estaba vigente al CIERRE de `date` (inclusive)?
 * Se decide por fechas, no por el estado de hoy: un plan dado de baja la
 * semana pasada estuvo activo en todas las fechas anteriores a su EffectiveTo.
 *   - effectiveFrom (date part) <= date
 *   - con effectiveTo: estado activo o de churn, y effectiveTo (date part) > date
 *   - sin effectiveTo: solo si hoy está activo (una baja sin fecha no se puede
 *     ubicar en el tiempo, así que no cuenta en ningún punto)
 */
export function isActiveAt(row: PurchaserPlanRaw, date: string): boolean {
  if (toDateOnly(row.effectiveFrom) > date) return false;
  if (row.effectiveTo === null) return ACTIVE_STATUSES.has(row.status);
  if (!ACTIVE_STATUSES.has(row.status) && !CHURN_STATUSES.has(row.status)) return false;
  return toDateOnly(row.effectiveTo) > date;
}

/** Count of active subscribers per plan at the CLOSE of `date` (see isActiveAt). */
export function activesAtDate(rows: PurchaserPlanRaw[], date: string): Record<string, number> {
  const result: Record<string, number> = {};
  for (const row of rows) {
    if (!isActiveAt(row, date)) continue;
    const plan = row.plan ?? 'Desconocido';
    result[plan] = (result[plan] ?? 0) + 1;
  }
  return result;
}

/**
 * Generate ordered buckets for a date range and granularity.
 * `start` is inclusive, `end` is EXCLUSIVE (i.e., next bucket's start).
 */
export function bucketize(range: DateRange, granularity: Granularity): Bucket[] {
  const buckets: Bucket[] = [];

  if (granularity === 'month') {
    let cursor = startOfMonth(range.from);
    while (cursor <= range.to) {
      const end = startOfMonth(addMonths(cursor, 1));
      const d = toUTC(cursor);
      const label = `${MES_LABELS[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(-2)}`;
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      buckets.push({ start: cursor, end, label, key });
      cursor = startOfMonth(addMonths(cursor, 1));
    }
  } else if (granularity === 'week') {
    let cursor = startOfISOWeek(range.from);
    while (cursor <= range.to) {
      const end = addDays(cursor, 7);
      const { year, week } = isoWeek(cursor);
      const label = `S${week} ${String(year).slice(-2)}`;
      const key = `${year}-W${String(week).padStart(2, '0')}`;
      buckets.push({ start: cursor, end, label, key });
      cursor = end;
    }
  } else {
    // day
    let cursor = range.from;
    while (cursor <= range.to) {
      const end = addDays(cursor, 1);
      const [, m, d] = cursor.split('-');
      const label = `${d}/${m}`;
      buckets.push({ start: cursor, end, label, key: cursor });
      cursor = end;
    }
  }

  return buckets;
}

/**
 * Altas rows per plan in a bucket.
 * Rows where effectiveFrom (date part) falls in [bucket.start, bucket.end) and plan is not null.
 */
export function altasRowsInBucket(rows: PurchaserPlanRaw[], bucket: Bucket): Record<string, PurchaserPlanRaw[]> {
  const result: Record<string, PurchaserPlanRaw[]> = {};
  for (const row of rows) {
    if (row.plan === null) continue;
    const from = toDateOnly(row.effectiveFrom);
    if (from >= bucket.start && from < bucket.end) {
      (result[row.plan] = result[row.plan] ?? []).push(row);
    }
  }
  return result;
}

/**
 * Bajas rows per plan in a bucket.
 * Rows where effectiveTo (date part) falls in [bucket.start, bucket.end) and status is a churn status.
 */
export function bajasRowsInBucket(rows: PurchaserPlanRaw[], bucket: Bucket): Record<string, PurchaserPlanRaw[]> {
  const result: Record<string, PurchaserPlanRaw[]> = {};
  for (const row of rows) {
    if (!CHURN_STATUSES.has(row.status)) continue;
    if (row.effectiveTo === null) continue;
    const to = toDateOnly(row.effectiveTo);
    if (to >= bucket.start && to < bucket.end) {
      const plan = row.plan ?? 'Desconocido';
      (result[plan] = result[plan] ?? []).push(row);
    }
  }
  return result;
}

function countByPlan(byPlan: Record<string, PurchaserPlanRaw[]>): Record<string, number> {
  const result: Record<string, number> = {};
  for (const [plan, list] of Object.entries(byPlan)) result[plan] = list.length;
  return result;
}

/** Altas per plan in a bucket (see altasRowsInBucket). */
export function altasInBucket(rows: PurchaserPlanRaw[], bucket: Bucket): Record<string, number> {
  return countByPlan(altasRowsInBucket(rows, bucket));
}

/** Bajas per plan in a bucket (see bajasRowsInBucket). */
export function bajasInBucket(rows: PurchaserPlanRaw[], bucket: Bucket): Record<string, number> {
  return countByPlan(bajasRowsInBucket(rows, bucket));
}

/**
 * Subscriber line for tooltips: "Apellido, Nombre · DNI".
 * Falls back to the full name (SF Name) when first/last name are missing.
 * When the member is not who bought the plan, adds "(titular: Apellido)".
 */
export function suscriptorLabel(row: PurchaserPlanRaw): string {
  const last = row.suscriptorLastName?.trim();
  const first = row.suscriptorFirstName?.trim();
  const nombre = last
    ? (first ? `${last}, ${first}` : last)
    : (row.suscriptorName?.trim() || 'Sin nombre');
  const linea = row.suscriptorDoc ? `${nombre} · ${row.suscriptorDoc}` : nombre;
  const titular = row.titularLastName?.trim() || row.titularFirstName?.trim();
  const esOtro = titular && row.titularDoc && row.suscriptorDoc && row.titularDoc !== row.suscriptorDoc;
  return esOtro ? `${linea} (titular: ${titular})` : linea;
}

/**
 * The most recent ManualRevenueEntry with effective_date <= date, or null if none.
 */
export function latestManualForDate(entries: ManualRevenueEntry[], date: string): ManualRevenueEntry | null {
  const eligible = entries
    .filter((e) => e.effective_date <= date)
    .sort((a, b) => b.effective_date.localeCompare(a.effective_date));
  return eligible[0] ?? null;
}

/**
 * Override manual SOLO si hay una entry cuya effective_date cae en el MISMO MES
 * del snapshot (y es <= snapshot). Si no, devuelve null y deberíamos usar el
 * cálculo automático. Esto evita que un manual viejo "pise" todos los meses
 * futuros indefinidamente.
 */
export function manualOverrideForMonth(
  entries: ManualRevenueEntry[],
  snapshotDate: string,
): ManualRevenueEntry | null {
  const monthStart = startOfMonth(snapshotDate);
  const eligible = entries
    .filter((e) => e.effective_date >= monthStart && e.effective_date <= snapshotDate)
    .sort((a, b) => b.effective_date.localeCompare(a.effective_date));
  return eligible[0] ?? null;
}

// ---------------------------------------------------------------------------
// Exclusive 8080 — cálculo automático de revenue por tabla de edad
// ---------------------------------------------------------------------------

/**
 * Tabla de valor mensual por tramo etario del plan Exclusive 8080.
 * Bordes inclusivos en ambos extremos. >80 cobra el último tramo (220k).
 */
export const EXCLUSIVE_8080_BRACKETS: ReadonlyArray<{ min: number; max: number; value: number }> = [
  { min: 0,  max: 29, value:  32000 },
  { min: 30, max: 39, value:  60000 },
  { min: 40, max: 49, value:  70000 },
  { min: 50, max: 59, value:  80000 },
  { min: 60, max: 64, value: 120000 },
  { min: 65, max: 69, value: 140000 },
  { min: 70, max: 74, value: 170000 },
  { min: 75, max: 80, value: 220000 },
];

/** Edad cumplida al `refDate` (YYYY-MM-DD), o null si la fecha de nacimiento es null/invalida. */
export function ageAtDate(birthdate: string | null, refDate: string): number | null {
  if (!birthdate) return null;
  const bd = birthdate.slice(0, 10); // por si viene timestamp
  if (!/^\d{4}-\d{2}-\d{2}$/.test(bd)) return null;
  const [by, bm, bd2] = bd.split('-').map(Number);
  const [ry, rm, rd]  = refDate.split('-').map(Number);
  let age = ry - by;
  // Si en el mes/día del refDate aún no cumplió años, restamos uno
  if (rm < bm || (rm === bm && rd < bd2)) age -= 1;
  return age;
}

/** Valor mensual que paga un suscriptor del 8080 según edad. >80 cae en el último tramo. */
export function revenueFor8080Age(age: number): number {
  if (age < 0) return 0;
  for (const bracket of EXCLUSIVE_8080_BRACKETS) {
    if (age >= bracket.min && age <= bracket.max) return bracket.value;
  }
  // Mayores al último tramo: cobra el último (220k).
  return EXCLUSIVE_8080_BRACKETS[EXCLUSIVE_8080_BRACKETS.length - 1].value;
}

export interface Exclusive8080Detail {
  id: string;
  suscriptorName: string | null;
  suscriptorDoc: string | null;
  birthdate: string | null;
  age: number | null;
  /** Valor asignado según la tabla. 0 si no hay birthdate. */
  value: number;
  /** Etiqueta del bracket aplicado, por ejemplo "30-39" o "missing". */
  bracket: string;
}

export interface Exclusive8080AutoRevenue {
  /** Suma total auto-calculada de los activos del 8080. */
  revenue: number;
  /** Cantidad de activos del 8080 con birthdate válido. */
  withBirthdate: number;
  /** Cantidad de activos del 8080 SIN birthdate (warning). */
  missingBirthdate: number;
  /** Detalle por suscriptor (vigentes al snapshot, status activo). */
  details: Exclusive8080Detail[];
}

function bracketLabelForAge(age: number): string {
  for (const b of EXCLUSIVE_8080_BRACKETS) {
    if (age >= b.min && age <= b.max) return `${b.min}-${b.max}`;
  }
  const last = EXCLUSIVE_8080_BRACKETS[EXCLUSIVE_8080_BRACKETS.length - 1];
  return `>${last.max}`;
}

/**
 * Revenue auto del plan Exclusive 8080 al cierre de `snapshotDate`.
 * Cuenta SOLO suscriptores con plan === EXCLUSIVE_PLAN_NAME y status activo,
 * vigentes al `snapshotDate`. La edad se calcula al snapshotDate provisto
 * (el caller debería pasarle lastDayOfMonth para revenue mensual).
 *
 * Los activos del 8080 sin PersonBirthdate NO suman al revenue y se reportan
 * como `missingBirthdate` para que el dashboard muestre un warning.
 */
export function exclusive8080AutoRevenue(
  rows: PurchaserPlanRaw[],
  snapshotDate: string,
  planName: string,
): Exclusive8080AutoRevenue {
  let revenue = 0;
  let withBirthdate = 0;
  let missingBirthdate = 0;
  const details: Exclusive8080Detail[] = [];

  for (const row of rows) {
    if (row.plan !== planName) continue;
    if (!isActiveAt(row, snapshotDate)) continue;

    const age = ageAtDate(row.birthdate, snapshotDate);
    if (age === null) {
      missingBirthdate++;
      details.push({
        id: row.id,
        suscriptorName: row.suscriptorName,
        suscriptorDoc: row.suscriptorDoc,
        birthdate: row.birthdate,
        age: null,
        value: 0,
        bracket: 'missing'
      });
      continue;
    }
    const value = revenueFor8080Age(age);
    withBirthdate++;
    revenue += value;
    details.push({
      id: row.id,
      suscriptorName: row.suscriptorName,
      suscriptorDoc: row.suscriptorDoc,
      birthdate: row.birthdate,
      age,
      value,
      bracket: bracketLabelForAge(age)
    });
  }

  // Orden estable: primero los que tienen edad ascendente, luego los missing
  details.sort((a, b) => {
    if (a.age === null && b.age === null) return 0;
    if (a.age === null) return 1;
    if (b.age === null) return -1;
    return a.age - b.age;
  });

  return { revenue, withBirthdate, missingBirthdate, details };
}

/** Unique plan names from PurchaserPlanRaw rows (non-null only). */
export function planNamesFromRaw(rows: PurchaserPlanRaw[]): string[] {
  const set = new Set<string>();
  for (const row of rows) {
    if (row.plan !== null) set.add(row.plan);
  }
  return Array.from(set);
}
